import { clientId } from './clientId';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Metode yang menurut HTTP boleh membawa badan permintaan. */
const BERBADAN = new Set(['POST', 'PUT', 'PATCH']);

/**
 * Pembungkus fetch untuk /api/v1. Pesan error diambil apa adanya dari server —
 * §B5 sudah memastikan Viewer tidak pernah menerima stack trace, jadi tidak
 * perlu disaring lagi di sini.
 *
 * POST tanpa badan **tetap** dikirim sebagai `{}` `application/json`. Alasannya
 * bukan kerapian: Zoraxy meneruskan POST tanpa badan sebagai `Transfer-Encoding:
 * chunked` tanpa `Content-Type`, dan Fastify menghitung itu sebagai "ada
 * isinya, tipenya tidak dikenal" — jawabannya 415 "Unsupported Media Type".
 * Lewat `127.0.0.1:3001` permintaan yang sama berhasil (curl memakai
 * `Content-Length: 0`), jadi gejalanya hanya muncul di lingkungan berproxy,
 * yaitu satu-satunya yang dipakai orang. Terukur 22 Agu 2026: "Test main/sub
 * stream" selalu gagal, dan **logout diam-diam tidak pernah sampai** — sesinya
 * masih hidup di server sesudah tombolnya ditekan. Backend juga dipagari untuk
 * kasus ini (`index.ts`); dua-duanya dipasang supaya klien lain (TV kiosk,
 * curl) tidak jatuh ke lubang yang sama.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const berbadan = BERBADAN.has((init?.method ?? 'GET').toUpperCase());
  const body = init?.body ?? (berbadan ? '{}' : undefined);

  const res = await fetch(`/api/v1${path}`, {
    ...init,
    body,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const balasan = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(balasan?.message ?? `Request failed (${res.status})`, res.status);
  }
  return balasan as T;
}

/**
 * Badan yang **wajib** menyertai setiap penghapusan (§G17).
 *
 * Nama objek yang diketik ulang dan password pemakainya. Dua-duanya diperiksa
 * server; yang di klien cuma dialognya (`components/DialogHapus.tsx`).
 */
export interface KonfirmasiHapus {
  confirmName: string;
  password: string;
}

/** Satu jalan untuk seluruh tombol Delete — tidak ada DELETE tanpa badan lagi. */
export async function hapusTerkonfirmasi<T = { ok: true }>(
  path: string,
  konfirmasi: KonfirmasiHapus,
): Promise<T> {
  return api<T>(path, { method: 'DELETE', body: JSON.stringify(konfirmasi) });
}

/**
 * Warna tag — nama token §G3, bukan hex, dan **tidak satu pun warna status**.
 * Daftarnya ditegakkan server (`routes/tags.ts`); yang di sini cuma salinan
 * untuk tipe dan pemetaan kelas.
 */
export const TAG_COLORS = ['navy', 'primary', 'accent', 'tint', 'muted'] as const;
export type TagColor = (typeof TAG_COLORS)[number];

export interface CameraTag {
  id: string;
  name: string;
  color: TagColor;
}

export interface CameraTagWithCount extends CameraTag {
  /** Kamera **yang terlihat oleh peminta** (§F6) — bukan COUNT(*) mentah. */
  cameraCount: number;
}

export interface Camera {
  id: string;
  name: string;
  /** §G11.1 — selalu ada; kamera tanpa tag mengirim `[]`, bukan `undefined`. */
  tags: CameraTag[];
  /**
   * `null` untuk viewer (§F6, revisi 1 Sep 2026) — server mencabut seluruh cara
   * menjangkau perangkatnya, bukan sekadar tidak merendernya. Berlaku juga
   * untuk `webUrl`, `username`, `mainUrlMasked`, `subUrlMasked`, dan
   * `lastError`; yang terakhir karena `stderr` ffmpeg hampir selalu memuat URL
   * RTSP-nya utuh.
   */
  host: string | null;
  port: number;
  httpPort: number;
  httpSecure: boolean;
  /** Alamat web UI kamera, dirakit server (§G11). `null` untuk viewer. */
  webUrl: string | null;
  transport: 'tcp' | 'udp';
  mainPath: string;
  subPath: string | null;
  username: string | null;
  hasPassword: boolean;
  audioEnabled: boolean;
  /** §D2 — `true` = View and Record, `false` = View Only (tetap tampil, tidak direkam). */
  recordEnabled: boolean;
  streamFps: number;
  /** §D6 — `null` berarti ikut retensi global, bukan nol hari. */
  maxDay: number | null;
  /** §D2 — panjang berkas rekaman. `null` = ikut setelan global. */
  segmentSeconds: number | null;
  isEnabled: boolean;
  /**
   * §D10.2 — `null` = tidak dalam pemeliharaan.
   *
   * Terpisah dari `status` dengan sengaja: kamera yang sengaja dimatikan orang
   * tidak boleh terlihat sama dengan kamera yang mati sendiri, dan itu justru
   * seluruh gunanya mode ini.
   */
  maintenanceSince?: string | null;
  maintenanceNote?: string | null;
  /**
   * Catatan bebas milik kamera ini (§G11, 8 Sep 2026).
   *
   * **Bukan `maintenanceNote`.** Yang itu menerangkan pemeliharaan yang sedang
   * berlangsung dan ikut hilang begitu selesai; yang ini bertahan — "PoE-nya di
   * switch lantai 2 port 7", "lensa berembun tiap hujan" — dan justru itu
   * gunanya.
   *
   * Diterima semua peran, termasuk viewer: isinya keterangan tentang kameranya
   * sebagai benda di lapangan, bukan cara menjangkau perangkatnya.
   */
  remarks: string | null;
  status: 'online' | 'offline' | 'error';
  lastError: string | null;
  measuredMbps: number | null;
  /**
   * Nama folder rekaman di NAS (§B4). `null` untuk viewer.
   *
   * Acak dan tidak menyebut nama kamera sejak 7 Sep 2026, jadi inilah
   * satu-satunya jalan dari kamera ke berkas mentahnya di NAS.
   */
  folderName: string | null;
  mainUrlMasked: string | null;
  subUrlMasked: string | null;
  /**
   * Konteks kamera padam (§D9.3). `undefined` = belum dimuat oleh endpoint
   * yang dipakai; `null` di dalamnya = sudah dicari, memang tidak ada.
   *
   * Dua keadaan yang berbeda, dan menyamakannya membuat tile menulis "belum
   * pernah tersambung" untuk kamera yang datanya cuma tidak ikut diminta.
   */
  lastRecordingAt?: string | null;
  recorder?: { state: string; attempt: number; retryInMs?: number } | null;
  /**
   * Kamera privat (§F8.2): hanya `owners` yang boleh melihat gambarnya. `null` = kamera
   * biasa. Pengelola melihat kamera privat orang lain lewat `/cameras?kelola=1`
   * — tanpa gambar.
   */
  private: { owners: { id: string; username: string; name: string }[] } | null;
}

/**
 * Tata letak gridview §G8, ditulis `kolom×baris`.
 *
 * Tinggal di sini, bukan di halaman dashboard, karena nilainya ikut dikirim ke
 * `PUT /auth/me` sebagai preferensi akun — daftarnya kontrak dengan server, dan
 * `enum GridLayout` di Prisma harus memuat nama yang sama persis.
 *
 * Sampai 22 Agu 2026 semuanya bujur sangkar. Ponsel mematahkan itu: layar
 * 390×844 yang diisi 2×2 memberi empat tile selebar ±190px dan menyisakan
 * setengah tinggi layar kosong — rasio tile terkunci 16:9 (§G7 no.4), jadi kolom
 * kedua membuat tile pendek, bukan layar penuh.
 */
/**
 * Seluruh nama tata letak yang dikenal, `kolom×baris` (§G8).
 *
 * Ini daftar **nilai yang sah**, bukan daftar yang ditawarkan: yang muncul di
 * bilah kendali dihitung dari bentuk layar oleh `lib/gridFit.ts`. Urutannya
 * mengikuti enum `grid_layout` di §E supaya dua tempat itu gampang dibandingkan.
 */
export const GRID_LAYOUTS = [
  '1x1',
  '1x2',
  '1x3',
  '1x4',
  '2x1',
  '2x2',
  '2x3',
  '2x4',
  '3x2',
  '3x3',
  '3x4',
  '4x3',
  '4x4',
  '4x5',
  '5x4',
  '5x5',
  '6x5',
] as const;

export type GridLayout = (typeof GRID_LAYOUTS)[number];

/** `'1x4'` → `{ kolom: 1, baris: 4 }`. */
export function gridDim(l: GridLayout): { kolom: number; baris: number } {
  const bagian = l.split('x');
  return { kolom: Number(bagian[0]), baris: Number(bagian[1]) };
}

/** Jumlah tile yang muat di satu halaman tata letak ini. */
export function gridSel(l: GridLayout): number {
  const { kolom, baris } = gridDim(l);
  return kolom * baris;
}

export type StreamProfile = 'main' | 'sub';

/**
 * Laju frame yang boleh diminta klien (§D3.2). `null` = laju penuh kamera.
 *
 * Yang dijarangkan server adalah pengiriman ke tab ini saja — satu ffmpeg tile
 * tetap melayani semua orang, dan penonton lain kamera yang sama tidak ikut
 * melambat.
 */
export type StreamFps = 1 | 2 | null;

/** Foto profil, atau `null` kalau orangnya belum memasang satu pun. */
export function avatarUrl(
  userId: string,
  hasAvatar: boolean,
  versi?: string | number | null,
): string | null {
  if (!hasAvatar) return null;
  /**
   * `?v=` bukan pemecah cache sekali pakai, melainkan **identitas gambarnya**
   * (`PublicUser.avatarVersion`, sidik isi berkas). Dua akibatnya:
   *
   * - Foto yang baru diganti punya URL baru, jadi ia langsung terlihat di
   *   setiap `<img>` yang menampilkannya — bukan cuma di kartu tempat
   *   menggantinya, dan tetap benar sesudah muat ulang.
   * - Foto yang **tidak** berubah punya URL yang sama persis, jadi server boleh
   *   menyuruh browser menyimpannya selamanya (`immutable`).
   *
   * Sebelum ini penandanya `Date.now()` yang hanya dipasang di halaman Profile,
   * dan gambarnya di-cache 60 detik tanpa ETag — sumber laporan "foto profil
   * tidak tersimpan" padahal tersimpan.
   */
  return `/api/v1/users/${userId}/avatar${versi ? `?v=${encodeURIComponent(String(versi))}` : ''}`;
}

/**
 * JPEG tunggal untuk thumbnail (§C2). Diambil dari frame terakhir worker tile
 * yang memang sudah hidup — tidak ada ffmpeg baru yang dijalankan untuknya.
 */
export function snapshotUrl(cameraId: string, nonce: number): string {
  return `/api/v1/snapshot/${cameraId}?t=${nonce}`;
}

/** Balasan `GET /stream/{id}/{profile}/ready` (§D3.1 langkah 3). */
export interface StreamReady {
  profile: StreamProfile;
  ready: boolean;
  state: string;
  message: string | null;
  fallbackProfile: StreamProfile | null;
}

/**
 * URL stream MJPEG untuk `<img src>`.
 *
 * `nonce` memaksa URL yang berbeda setiap kali tile dinyalakan ulang. Tanpa itu
 * browser bisa memakai kembali koneksi yang baru saja dibatalkan untuk URL yang
 * sama, dan tile tampil beku di frame terakhir sebelum dimatikan.
 */
export function streamUrl(
  cameraId: string,
  profile: StreamProfile,
  nonce: number,
  fps: StreamFps = null,
): string {
  // `c=` adalah id per-tab (§D3.4): server memakainya untuk mengakhiri aliran
  // lama milik tab yang sama, dan untuk tahu tab ini masih hidup lewat
  // heartbeat. Tanpa itu tiap pergantian tata letak menumpuk aliran baru di
  // atas yang lama — terukur 21 Agu 2026: 13 aliran yatim, 36 Mbps, nol
  // browser terbuka.
  // `fps=` hanya dikirim kalau memang diminta lebih rendah: URL tanpa parameter
  // itu adalah URL yang sama dengan sebelum §D3.2 ada, sehingga klien lama dan
  // TV pos satpam tidak berubah perilakunya sama sekali.
  const laju = fps ? `&fps=${fps}` : '';
  return `/api/v1/stream/${cameraId}/${profile}?t=${nonce}&c=${encodeURIComponent(
    clientId(),
  )}${laju}`;
}

export interface Recording {
  id: string;
  cameraId: string;
  fileName: string;
  /** ISO 8601 UTC (§B2). Ditampilkan sebagai WIB oleh formatter di UI. */
  startedAt: string;
  endedAt: string;
  durationS: number;
  sizeBytes: number;
  isComplete: boolean;
  /**
   * Lebar lembar sprite dalam sel — **geometri berkasnya**, bukan jumlah menit
   * yang ada rekamannya (§D8.1, 8 Sep 2026).
   *
   * Dulu angka ini dihitung sendiri di klien dari `durationS`, dan itu benar
   * selama sprite hanya lahir dari segmen yang sudah ditutup. Sprite sementara
   * untuk segmen yang masih ditulis mematahkannya: lembarnya selalu selebar
   * **nominal** segmen walau baru tiga menit terisi, karena lembar yang
   * menyempit-melebar tiap menit membuat tiap sel menunjuk gambar yang salah.
   *
   * Jadi: `spriteCells` menentukan `background-size`, `durationS` menentukan
   * berapa sel yang pantas ditampilkan. Dua pertanyaan berbeda.
   */
  spriteCells: number;
}

export interface RecordingPage {
  recordings: Recording[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

/** Satu segmen di timeline (§D8.1) — sama seperti `Recording`, plus posisinya. */
export interface TimelineSegment extends Recording {
  /**
   * Posisi segmen ini di dalam putaran gabungan, dalam detik.
   *
   * Dua sistem koordinat hidup berdampingan di halaman playback, dan
   * membedakannya itu penting: **jam dinding** (yang dilihat orang, dan yang
   * memuat bolong) dan **waktu concat** (yang dilihat pemutar, tempat bolong
   * tidak ada sama sekali). `offsetS` adalah jembatan di antara keduanya.
   */
  offsetS: number;
}

export interface TimelineGap {
  startedAt: string;
  endedAt: string;
  durationS: number;
}

/** Balasan `GET /recordings/timeline` (§D8.1). */
export interface Timeline {
  cameraId: string;
  from: string;
  to: string;
  segments: TimelineSegment[];
  gaps: TimelineGap[];
  /** Durasi rekaman yang benar-benar ada — bukan panjang rentang. */
  coveredS: number;
  truncated: boolean;
}

/** Balasan `GET /cameras/{id}/health` (§D9). */
export interface CameraHealth {
  cameraId: string;
  name: string;
  status: 'online' | 'offline' | 'error';
  isEnabled: boolean;
  /** `false` = View Only (§D2) — kamera ini memang tidak merekam. */
  recordEnabled: boolean;
  /** Hanya terisi untuk Admin (§D7.3). */
  lastError: string | null;
  /** Waktu **tersambung**, bukan frame terakhir — lihat §D9.1. */
  connectedAt: string | null;
  frame: { lastAt: string | null; lagS: number | null };
  bitrate: { mbps: number | null; gbPerDay: number | null };
  coverage: {
    windowS: number;
    coveredS: number;
    ratio: number;
    gaps: number;
    lastGap: { startedAt: string; endedAt: string; durationS: number } | null;
    /** `false` = kamera belum genap 24 jam; rasionya dibaca hati-hati. */
    fullWindow: boolean;
  };
  storage: { segments: number; bytes: number; maxDay: number };
  events: {
    level: string;
    event: string;
    message: string;
    lastOccurredAt: string;
    occurrenceCount: number;
  }[];
}

/** Satu permintaan export (§D8.2, §D8.3). */
export interface ExportJob {
  id: string;
  cameraId: string;
  requestedFrom: string;
  requestedTo: string;
  /** Jam SEBENARNYA berkas hasil sesudah pembulatan keyframe — ini yang ditampilkan. */
  actualFrom: string | null;
  actualTo: string | null;
  sizeBytes: number | null;
  /** §D8.8 — `pending` dipecah jadi `queued` dan `running`. */
  status: 'queued' | 'running' | 'ready' | 'failed' | 'cancelled';
  error: string | null;
  downloadedAt: string | null;
  createdAt: string;
  /** Nama berkas pilihan pemakai (§D8.8). `null` = pakai nama bawaan. */
  name: string | null;
  cameraName: string | null;
  readyAt: string | null;
  /**
   * Berkasnya sudah disapu, barisnya masih ada (§D8.9) — **keadaan turunan**,
   * bukan nilai `status`: `ready` dengan `file_path` yang sudah kosong.
   */
  removed: boolean;
  removedAt: string | null;
  /** Dari Redis, hanya saat `running`/`queued`. `null` = prosesnya tidak melapor. */
  progressPct?: number | null;
  /** Nomor urut FIFO seluruh sistem, hanya saat `queued`. */
  queuePosition?: number | null;
}

export async function listExports(): Promise<ExportJob[]> {
  const r = await api<{ exports: ExportJob[] }>('/exports');
  return r.exports;
}

/**
 * Batalkan yang masih antre/berjalan, atau buang barisnya kalau sudah selesai.
 *
 * `konfirmasi` hanya perlu untuk yang kedua (§G17). Membatalkan pekerjaan yang
 * belum menghasilkan apa-apa tidak menghapus apa pun — kalau ternyata salah,
 * tinggal diminta lagi dengan rentang yang sama.
 */
export async function dropExport(id: string, konfirmasi?: KonfirmasiHapus): Promise<void> {
  await api(`/exports/${id}`, {
    method: 'DELETE',
    ...(konfirmasi ? { body: JSON.stringify(konfirmasi) } : {}),
  });
}

/**
 * Waktu selalu disimpan UTC dan **ditampilkan** WIB (§B2). Formatter dipatok ke
 * `Asia/Jakarta`, bukan zona browser: operator yang membuka dari laptop yang
 * jamnya belum diatur tetap melihat waktu yang sama dengan yang ada di NAS.
 */
const WIB = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jakarta',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export function formatWib(iso: string): string {
  return WIB.format(new Date(iso));
}

/** Jam:menit:detik WIB — dipakai sumbu timeline, tempat tanggal penuh tidak muat. */
const WIB_JAM = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jakarta',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export function formatWibJam(ms: number): string {
  return WIB_JAM.format(new Date(ms));
}

/** Jam:menit saja — label sumbu yang rapat. */
const WIB_MENIT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jakarta',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatWibMenit(ms: number): string {
  return WIB_MENIT.format(new Date(ms));
}

/**
 * Tanggal saja — "Mon 31 Aug".
 *
 * Timeline playback selama ini cuma menyebut jam, dan jam tanpa tanggal
 * ambigu begitu sumbunya melewati tengah malam: rekaman tujuh hari punya tujuh
 * "19:20" yang kelihatan persis sama (dilaporkan 31 Agu 2026). Hari ikut
 * disebut karena orang mencari kejadian dengan hari — "Sabtu sore" — bukan
 * dengan tanggal.
 */
const WIB_TANGGAL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jakarta',
  weekday: 'short',
  day: '2-digit',
  month: 'short',
});

export function formatWibTanggal(ms: number): string {
  return WIB_TANGGAL.format(new Date(ms));
}

/** Tanggal WIB sebagai `YYYY-MM-DD` — dipakai membandingkan "hari yang sama". */
const WIB_HARI = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Jakarta',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function hariWib(ms: number): string {
  return WIB_HARI.format(new Date(ms));
}

export function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
}

export interface TestResult {
  ok: boolean;
  profile: string;
  url: string;
  message?: string;
  video?: { codec: string; width: number | null; height: number | null; fps: number | null; isHevc: boolean };
  hasAudio?: boolean;
}

/* ── Camera Tags (§G11.1) ─────────────────────────────────────────────── */

export async function listTags(): Promise<CameraTagWithCount[]> {
  const r = await api<{ tags: CameraTagWithCount[] }>('/tags');
  return r.tags;
}

export async function createTag(name: string, color: TagColor): Promise<CameraTag> {
  const r = await api<{ tag: CameraTag }>('/tags', {
    method: 'POST',
    body: JSON.stringify({ name, color }),
  });
  return r.tag;
}

export async function updateTag(
  id: string,
  patch: { name?: string; color?: TagColor },
): Promise<CameraTag> {
  const r = await api<{ tag: CameraTag }>(`/tags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return r.tag;
}

/** Membalas berapa kamera kehilangan tag ini — dipakai kalimat sesudah hapus. */
export async function deleteTag(id: string, konfirmasi: KonfirmasiHapus): Promise<number> {
  const r = await hapusTerkonfirmasi<{ camerasAffected: number }>(`/tags/${id}`, konfirmasi);
  return r.camerasAffected;
}

/* ── Camera Activity Timeline (§D7.4) ─────────────────────────────────── */

export type ActivityState =
  | 'online'
  | 'offline'
  | 'reconnecting'
  | 'problem'
  | 'maintenance'
  | 'unstable';

export interface ActivityEpisode {
  /** `instant` tidak pernah mengklaim durasi — penggantian perangkat, ganti setelan. */
  kind: 'state' | 'instant';
  state: ActivityState | null;
  event: string;
  message: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number;
  ongoing: boolean;
  occurrences: number;
  /** Episode ini sudah berjalan sebelum jendela dimulai (§D7.4). */
  startedBeforeWindow: boolean;
  /** Isi episode `unstable`, supaya barisnya bisa dibentangkan (§G13). */
  collapsed?: {
    state: ActivityState | null;
    event: string;
    message: string;
    startedAt: string;
    endedAt: string | null;
    occurrences: number;
  }[];
}

/* ── Incident Notes (§D11) ────────────────────────────────────────────── */

export interface IncidentNote {
  id: string;
  cameraId: string | null;
  cameraName: string | null;
  author: string;
  authorUserId: string | null;
  title: string;
  body: string | null;
  startedAt: string;
  /** `null` = masih berlangsung — inilah yang dibaca saat pergantian shift. */
  endedAt: string | null;
  ongoing: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function listIncidents(opts?: {
  cameraId?: string;
  open?: boolean;
}): Promise<IncidentNote[]> {
  const q = new URLSearchParams();
  if (opts?.cameraId) q.set('camera_id', opts.cameraId);
  if (opts?.open) q.set('open', '1');
  const qs = q.toString();
  const r = await api<{ incidents: IncidentNote[] }>(`/incidents${qs ? `?${qs}` : ''}`);
  return r.incidents;
}

export interface IncidentDraft {
  cameraId?: string | null;
  title?: string;
  body?: string | null;
  startedAt?: string;
  endedAt?: string | null;
}

export async function saveIncident(id: string | null, draft: IncidentDraft): Promise<IncidentNote> {
  const r = await api<{ incident: IncidentNote }>(id ? `/incidents/${id}` : '/incidents', {
    method: id ? 'PATCH' : 'POST',
    body: JSON.stringify(draft),
  });
  return r.incident;
}

export async function deleteIncident(id: string, konfirmasi: KonfirmasiHapus): Promise<void> {
  await hapusTerkonfirmasi(`/incidents/${id}`, konfirmasi);
}

export interface CameraActivity {
  camera: {
    id: string;
    name: string;
    status: 'online' | 'offline' | 'error';
    recordEnabled: boolean;
    isEnabled: boolean;
    createdAt: string;
  };
  from: string;
  to: string;
  /** `true` = jendelanya memang sepi, bukan datanya yang hilang (§D7.4). */
  quiet: boolean;
  truncated: boolean;
  episodes: ActivityEpisode[];
  /** Pita cerita manusia di atas rangkaian keadaan (§D11). Kosong untuk Viewer. */
  incidents: IncidentNote[];
}

export async function fetchCameraActivity(
  cameraId: string,
  range?: { from?: string; to?: string },
): Promise<CameraActivity> {
  const q = new URLSearchParams();
  if (range?.from) q.set('from', range.from);
  if (range?.to) q.set('to', range.to);
  const qs = q.toString();
  return api<CameraActivity>(`/cameras/${cameraId}/activity${qs ? `?${qs}` : ''}`);
}

/* ── Configuration Template (§G11.4) ──────────────────────────────────── */

/**
 * Nilai yang bisa ditemplatkan. Kuncinya camelCase, sama dengan badan JSON di
 * seluruh API — daftar putihnya ditegakkan server (`services/cameraTemplate.ts`).
 *
 * **Kunci yang ADA di sini yang ditimpa.** `maxDay: null` (ikut global) dan
 * "tidak ditemplatkan" dua hal berbeda, jadi tiap field punya saklarnya sendiri
 * di editor dan yang dimatikan tidak masuk objek ini sama sekali.
 */
export interface TemplateValues {
  transport?: 'tcp' | 'udp';
  port?: number;
  httpPort?: number;
  httpSecure?: boolean;
  mainPath?: string;
  subPath?: string | null;
  username?: string | null;
  audioEnabled?: boolean;
  recordEnabled?: boolean;
  maxDay?: number | null;
  segmentSeconds?: number | null;
  tagIds?: string[];
}

/** Kunci yang boleh ada di template, urut seperti di form kamera. */
export const TEMPLATE_KEYS = [
  'transport',
  'port',
  'httpPort',
  'httpSecure',
  'mainPath',
  'subPath',
  'username',
  'audioEnabled',
  'recordEnabled',
  'maxDay',
  'segmentSeconds',
  'tagIds',
] as const satisfies readonly (keyof TemplateValues)[];

export type TemplateKey = (typeof TEMPLATE_KEYS)[number];

export interface CameraTemplate {
  id: string;
  name: string;
  description: string | null;
  values: TemplateValues;
  /** Ada-tidaknya kredensial, bukan isinya (§F5). */
  hasPassword: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function listTemplates(): Promise<CameraTemplate[]> {
  const r = await api<{ templates: CameraTemplate[] }>('/camera-templates');
  return r.templates;
}

export interface TemplateDraft {
  name: string;
  description: string | null;
  values: TemplateValues;
  /** Tidak dikirim = biarkan apa adanya; string = ganti; null = hapus. */
  password?: string | null;
}

export async function saveTemplate(
  id: string | null,
  draft: TemplateDraft,
): Promise<CameraTemplate> {
  const r = await api<{ template: CameraTemplate }>(
    id ? `/camera-templates/${id}` : '/camera-templates',
    { method: id ? 'PUT' : 'POST', body: JSON.stringify(draft) },
  );
  return r.template;
}

export async function deleteTemplate(id: string, konfirmasi: KonfirmasiHapus): Promise<void> {
  await hapusTerkonfirmasi(`/camera-templates/${id}`, konfirmasi);
}

/* ── Bulk Operation (§G11.2) ──────────────────────────────────────────── */

export type BulkAction =
  | 'set_retention'
  | 'set_record'
  | 'add_tags'
  | 'remove_tags'
  | 'apply_template'
  | 'test'
  | 'restart'
  | 'set_maintenance';

export interface BulkResult {
  cameraId: string;
  name: string;
  ok: boolean;
  message?: string;
}

export interface BulkResponse {
  action: BulkAction;
  /** **Selalu daftar**, bahkan saat semuanya berhasil (§G11.2). */
  results: BulkResult[];
  okCount: number;
  failCount: number;
}

export type BulkRequest =
  | { action: 'set_retention'; cameraIds: string[]; maxDay: number | null }
  | { action: 'set_record'; cameraIds: string[]; recordEnabled: boolean }
  | { action: 'add_tags' | 'remove_tags'; cameraIds: string[]; tagIds: string[] }
  | { action: 'apply_template'; cameraIds: string[]; templateId: string }
  | { action: 'test' | 'restart'; cameraIds: string[] }
  | { action: 'set_maintenance'; cameraIds: string[]; on: boolean; note: string | null };

export async function runBulk(body: BulkRequest): Promise<BulkResponse> {
  return api<BulkResponse>('/cameras/bulk', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Kunci template yang, kalau ikut diterapkan, membuat recorder-nya restart —
 * dan restart memotong ekor segmen yang sedang ditulis (README).
 *
 * Disalin dari `routes/cameraBulk.ts` dengan sengaja **hanya untuk memutuskan
 * apakah peringatannya muncul**; yang menentukan perilaku tetap server. Kalau
 * daftarnya nanti berbeda, yang terjadi paling buruk adalah peringatan yang
 * kurang atau kelebihan satu baris — bukan operasi yang salah.
 */
const KUNCI_MEMICU_RESTART: (keyof TemplateValues)[] = [
  'port',
  'transport',
  'mainPath',
  'username',
  'audioEnabled',
  'segmentSeconds',
];

export function templateMemicuRestart(values: TemplateValues): boolean {
  return KUNCI_MEMICU_RESTART.some((k) => k in values);
}

/* ── Bulk Import (§G11.3) ─────────────────────────────────────────────── */

/**
 * Satu baris impor. `template` dan `tags` disebut menurut **namanya**, bukan
 * id: berkasnya ditulis manusia di Excel, dan angka `7` di kolom template tidak
 * bisa diperiksa siapa pun sebelum diunggah — `Kamera POP` bisa.
 */
export interface ImportRow {
  name: string;
  host: string;
  port?: number;
  httpPort?: number;
  httpSecure?: boolean;
  transport?: 'tcp' | 'udp';
  mainPath?: string;
  subPath?: string | null;
  username?: string | null;
  password?: string | null;
  audioEnabled?: boolean;
  recordEnabled?: boolean;
  maxDay?: number | null;
  segmentSeconds?: number | null;
  isEnabled?: boolean;
  template?: string | null;
  tags?: string[];
}

export interface ImportIssue {
  /** `error` menggugurkan barisnya; `warning` tidak. */
  severity: 'error' | 'warning';
  field?: string;
  message: string;
}

export interface ImportPlanRow {
  index: number;
  name: string;
  host: string;
  mainPath: string | null;
  transport: 'tcp' | 'udp';
  port: number;
  maxDay: number | null;
  segmentSeconds: number | null;
  recordEnabled: boolean;
  tagCount: number;
  hasPassword: boolean;
  /** Nilai turunan yang tidak bisa diubah sesudah kameranya jadi. */
  folderName: string;
  issues: ImportIssue[];
  ok: boolean;
}

export interface ImportValidation {
  rows: ImportPlanRow[];
  okCount: number;
  errorCount: number;
}

export interface ImportTestResult {
  index: number;
  name: string;
  ok: boolean;
  message?: string;
}

export interface ImportCommitResult {
  results: { index: number; name: string; ok: boolean; cameraId?: string; message?: string }[];
  createdCount: number;
  failedCount: number;
}

export const IMPORT_MAX_ROWS = 200;

export async function validateImport(rows: ImportRow[]): Promise<ImportValidation> {
  return api<ImportValidation>('/cameras/import/validate', {
    method: 'POST',
    body: JSON.stringify({ rows }),
  });
}

export async function testImport(
  rows: ImportRow[],
): Promise<{ results: ImportTestResult[]; skipped: number[] }> {
  return api('/cameras/import/test', { method: 'POST', body: JSON.stringify({ rows }) });
}

export async function commitImport(rows: ImportRow[]): Promise<ImportCommitResult> {
  return api<ImportCommitResult>('/cameras/import/commit', {
    method: 'POST',
    body: JSON.stringify({ rows }),
  });
}

/* ── Daily Health Summary & Wall Status (§D12, §G14) ──────────────────── */

export type HealthGolongan = 'healthy' | 'warning' | 'problem' | 'maintenance';

export interface HealthSummary {
  from: string;
  to: string;
  /** `fleet` = seluruh armada (staf); `assigned` = kamera milik orang ini (§F6). */
  scope: 'fleet' | 'assigned';
  total: number;
  healthy: number;
  warning: number;
  problem: number;
  maintenance: number;
  /** "Total recording session" — persen, 2 desimal. */
  uptimePct: number;
  cameras: {
    cameraId: string;
    name: string;
    golongan: HealthGolongan;
    cakupan: number | null;
    lagS: number | null;
  }[];
  incidentsOpen: number;
  /** `null` untuk Viewer — angka tingkat armada tidak punya arti bagi 8 dari 96 kamera. */
  storage: {
    usedGb: number | null;
    totalCapacityGb: number | null;
    freeGb: number | null;
    daysUntilFull: number | null;
    quota: { gb: number; usedGb: number; usedPercent: number; thresholdPercent: number } | null;
  } | null;
}

export async function fetchHealthSummary(): Promise<HealthSummary> {
  return api<HealthSummary>('/system/summary');
}

/* ── Bookmark rekaman (§D8.7) ─────────────────────────────────────────── */

export interface Bookmark {
  id: string;
  cameraId: string;
  cameraName: string;
  /** Waktu absolut UTC — bukan offset di dalam berkas (§D8.7). */
  at: string;
  label: string;
  /**
   * Rekamannya sudah lewat retensi: barisnya tetap ada, gambarnya tidak.
   *
   * **Bookmark yang jatuh di dalam bolong tidak ditandai `expired`** — "rekaman
   * sudah lewat retensi" dan "kamera sedang mati saat itu" dua fakta berbeda,
   * dan yang kedua sudah punya wujudnya sendiri di timeline (celah, §D8.1).
   */
  expired: boolean;
  createdAt: string;
}

export async function listBookmarks(opts: {
  cameraId?: string;
  from?: string;
  to?: string;
}): Promise<Bookmark[]> {
  const q = new URLSearchParams();
  if (opts.cameraId) q.set('camera_id', opts.cameraId);
  if (opts.from) q.set('from', opts.from);
  if (opts.to) q.set('to', opts.to);
  const qs = q.toString();
  const r = await api<{ bookmarks: Bookmark[] }>(`/bookmarks${qs ? `?${qs}` : ''}`);
  return r.bookmarks;
}

export async function createBookmark(
  cameraId: string,
  atMs: number,
  label: string,
): Promise<Bookmark> {
  const r = await api<{ bookmark: Bookmark }>('/bookmarks', {
    method: 'POST',
    body: JSON.stringify({ cameraId, at: new Date(atMs).toISOString(), label }),
  });
  return r.bookmark;
}

export async function renameBookmark(id: string, label: string): Promise<void> {
  await api(`/bookmarks/${id}`, { method: 'PATCH', body: JSON.stringify({ label }) });
}

export async function deleteBookmark(id: string, konfirmasi: KonfirmasiHapus): Promise<void> {
  await hapusTerkonfirmasi(`/bookmarks/${id}`, konfirmasi);
}

/* ── Maintenance mode aplikasi & storage (§D10.1) ─────────────────────── */

export interface SystemMaintenance {
  /** `app` = API/UI ditutup untuk non-admin; `storage` = perekaman dihentikan. */
  scope: 'app' | 'storage';
  message: string;
  since: string;
  by: string;
}

export async function fetchMaintenance(): Promise<SystemMaintenance | null> {
  const r = await api<{ maintenance: SystemMaintenance | null }>('/system/maintenance');
  return r.maintenance;
}

export async function startMaintenance(
  scope: 'app' | 'storage',
  message: string,
): Promise<SystemMaintenance> {
  const r = await api<{ maintenance: SystemMaintenance }>('/system/maintenance', {
    method: 'PUT',
    body: JSON.stringify({ scope, message }),
  });
  return r.maintenance;
}

export async function endMaintenance(): Promise<void> {
  await api('/system/maintenance', { method: 'DELETE' });
}

/* ── Maintenance per kamera & Replace camera (§D10.2) ─────────────────── */

export async function setCameraMaintenance(
  cameraId: string,
  note: string | null,
): Promise<Camera> {
  // `null` sebagai badan melepaskan; objek menyalakan (§D10.2).
  const r = await api<{ camera: Camera }>(`/cameras/${cameraId}/maintenance`, {
    method: 'PUT',
    body: note === null ? JSON.stringify(null) : JSON.stringify({ note }),
  });
  return r.camera;
}

export interface ReplaceDraft {
  host?: string;
  port?: number;
  httpPort?: number;
  httpSecure?: boolean;
  transport?: 'tcp' | 'udp';
  mainPath?: string;
  subPath?: string | null;
  username?: string | null;
  password?: string | null;
}

export async function replaceCamera(cameraId: string, draft: ReplaceDraft): Promise<Camera> {
  const r = await api<{ camera: Camera }>(`/cameras/${cameraId}/replace`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });
  return r.camera;
}

/* ── Isi pratayang yang bolong (§D8.1, 8 Sep 2026) ─────────────────────── */

export interface StatusIsiPratayang {
  aktif: boolean;
  /** `null` = seluruh armada. */
  cameraId: string | null;
  /** Berapa baris rekaman yang akan disisir seluruhnya. */
  total: number;
  diperiksa: number;
  /** Yang benar-benar diserahkan untuk dibuat — yang sudah ada tidak dihitung. */
  dibuat: number;
  dilewati: number;
  mulai: number;
}

/**
 * Minta worker mengisi pratayang yang masih bolong.
 *
 * `cameraId` `null` = seluruh armada (khusus admin). Jawabannya cuma "diterima"
 * — pekerjaannya sendiri berjalan di worker dan bisa makan berjam-jam, jadi
 * kemajuannya dibaca lewat `statusIsiPratayang`.
 */
export async function mintaIsiPratayang(cameraId: string | null): Promise<void> {
  // Dua endpoint, bukan satu dengan `cameraId: null`: yang seluruh armada
  // dijaga `requireAdmin` di server (§F8), dan pemisahan itu sengaja terlihat
  // sampai ke sini supaya tidak ada satu pun jalur klien yang bisa keliru
  // mengira keduanya setara.
  const path = cameraId === null ? '/recordings/sprite-fill/all' : '/recordings/sprite-fill';
  await api<{ ok: true }>(path, {
    method: 'POST',
    body: cameraId === null ? '{}' : JSON.stringify({ cameraId }),
  });
}

export async function statusIsiPratayang(): Promise<StatusIsiPratayang | null> {
  const r = await api<{ status: StatusIsiPratayang | null }>('/recordings/sprite-fill');
  return r.status;
}
